/**
 * Message Timing Engine — Simulation Immersion Layer (Phase 1)
 * -------------------------------------------------------------
 * Deterministic adaptive pacing for the rehearsal simulator.
 * Produces psychologically intentional response delays, typing
 * durations, hesitation odds, and silence gaps based on the live
 * behavioral runtime (pressure, withdrawal, volatility, style).
 *
 * No randomness theatre — every variance is grounded in trajectory
 * state. Designed to feel forensic, not gamified. Modular so future
 * phases can add voice cadence, biometric pacing, or audio pressure
 * atmospheres without rewriting consumers.
 */

import type { NextTurnStyle, PressureState } from "./behaviorRuntime";

export interface TimingInput {
  pressureState?: PressureState | null;
  /** 0–100 rolling pressure level */
  pressureLevel?: number;
  /** 0–100 emotional volatility */
  volatility?: number;
  /** 0–100 withdrawal weight */
  withdrawalLevel?: number;
  nextTurnStyle?: NextTurnStyle | null;
  /** Optional length of the agent's reply, used to scale typing time */
  responseLength?: number;
  /** Turn index for slow drift effects */
  turn?: number;
}

export interface TimingProfile {
  /** Delay BEFORE typing indicator appears (silence / hesitation gap, ms) */
  responseDelayMs: number;
  /** Duration the typing indicator is visible (ms) */
  typingDelayMs: number;
  /** 0–1 chance the typing indicator stutters (typing → stopped → resumed) */
  hesitationProbability: number;
  /** When true, agent should split reply into 2 staggered fragments */
  fragmented: boolean;
  /** Inter-fragment gap (ms) */
  fragmentGapMs: number;
  /** When true, simulator should display a "seen" / cold-gap signal */
  showSilenceSignal: boolean;
  /** Optional minutes-ago label for the silence signal */
  silenceLabel?: string;
  /** Tactical descriptor ("typing", "delayed", "silence", "burst") */
  cadence: "burst" | "typing" | "delayed" | "silence" | "fragmented";
}

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

const STYLE_BASELINE: Partial<
  Record<
    NextTurnStyle,
    {
      response: number;
      typing: number;
      hesitation: number;
      cadence: TimingProfile["cadence"];
      fragmented?: boolean;
    }
  >
> = {
  silence_pressure:      { response: 4200, typing: 1100, hesitation: 0.15, cadence: "silence" },
  passive_withdrawal:    { response: 2400, typing: 1500, hesitation: 0.45, cadence: "delayed" },
  emotional_exhaustion:  { response: 2100, typing: 2200, hesitation: 0.55, cadence: "delayed" },
  victim_framing:        { response: 1500, typing: 2000, hesitation: 0.40, cadence: "typing", fragmented: true },
  reality_reversal:      { response: 1200, typing: 1700, hesitation: 0.30, cadence: "typing" },
  guilt_reversal:        { response: 1700, typing: 1900, hesitation: 0.35, cadence: "typing" },
  shame_attack:          { response:  650, typing: 1100, hesitation: 0.10, cadence: "burst" },
  coercive_pressure:     { response:  800, typing: 1300, hesitation: 0.20, cadence: "burst" },
  tone_policing:         { response: 1200, typing: 1400, hesitation: 0.25, cadence: "typing" },
  subtle_minimization:   { response: 1600, typing: 1500, hesitation: 0.30, cadence: "typing" },
};

const STATE_MULTIPLIER: Partial<Record<PressureState, { delay: number; typing: number }>> = {
  resistance_escalating:    { delay: 0.7,  typing: 0.85 },
  emotional_reversal:       { delay: 1.25, typing: 1.20 },
  disengagement_forming:    { delay: 1.6,  typing: 0.9  },
  leverage_withdrawal:      { delay: 1.8,  typing: 0.85 },
  trajectory_unstable:      { delay: 0.85, typing: 1.0  },
  unstable_calm:            { delay: 1.1,  typing: 1.0  },
  emotional_ambiguity:      { delay: 1.0,  typing: 1.0  },
  accountability_avoidance: { delay: 0.9,  typing: 1.05 },
  stable:                   { delay: 1.0,  typing: 1.0  },
};

/**
 * Primary export — adaptive timing profile for the next agent turn.
 */
export function getMessageDelay(input: TimingInput): TimingProfile {
  const style = input.nextTurnStyle ?? "subtle_minimization";
  const base = STYLE_BASELINE[style] ?? {
    response: 1400,
    typing: 1500,
    hesitation: 0.25,
    cadence: "typing" as const,
  };
  const stateMul = STATE_MULTIPLIER[input.pressureState ?? "stable"] ?? { delay: 1, typing: 1 };

  const pressure = clamp(input.pressureLevel ?? 50, 0, 100);
  const volatility = clamp(input.volatility ?? 30, 0, 100);
  const withdrawal = clamp(input.withdrawalLevel ?? 30, 0, 100);

  // High pressure → faster reply. High withdrawal → slower. Volatility → hesitation jitter.
  const pressureScale = 1 - (pressure - 50) / 180;        // ~0.72–1.28
  const withdrawalScale = 1 + (withdrawal - 30) / 140;    // ~1.0–1.5
  const volatilityScale = 1 + (volatility - 40) / 220;    // mild

  let responseDelayMs = base.response * stateMul.delay * pressureScale * withdrawalScale;
  let typingDelayMs = base.typing * stateMul.typing * volatilityScale;

  // Scale typing time to message length when known (~22 ms/char, capped).
  if (input.responseLength && input.responseLength > 0) {
    const lenComponent = clamp(input.responseLength * 22, 600, 3600);
    typingDelayMs = (typingDelayMs + lenComponent) / 2;
  }

  // Withdrawal / silence states extend the cold gap further on later turns.
  if (style === "silence_pressure" || style === "passive_withdrawal") {
    responseDelayMs += Math.min(2400, (input.turn ?? 1) * 220);
  }

  // Burst cadence collapses delays.
  if (base.cadence === "burst") {
    responseDelayMs = Math.min(responseDelayMs, 900);
    typingDelayMs = Math.min(typingDelayMs, 1100);
  }

  const hesitationProbability = clamp(
    base.hesitation + (volatility - 40) / 200,
    0,
    0.85,
  );

  const showSilenceSignal =
    base.cadence === "silence" ||
    (base.cadence === "delayed" && responseDelayMs >= 2600) ||
    withdrawal >= 65;

  const silenceLabel = showSilenceSignal
    ? withdrawal >= 75
      ? "seen 4m ago"
      : withdrawal >= 55
      ? "seen 2m ago"
      : "seen just now"
    : undefined;

  const fragmented =
    !!base.fragmented ||
    (volatility >= 55 && pressure >= 55 && base.cadence !== "silence");

  return {
    responseDelayMs: Math.round(clamp(responseDelayMs, 350, 6500)),
    typingDelayMs: Math.round(clamp(typingDelayMs, 500, 4200)),
    hesitationProbability,
    fragmented,
    fragmentGapMs: fragmented ? Math.round(clamp(700 + volatility * 8, 700, 1800)) : 0,
    showSilenceSignal,
    silenceLabel,
    cadence: fragmented ? "fragmented" : base.cadence,
  };
}

/**
 * Split agent text into 1–2 fragments at a natural sentence boundary.
 * Used when the timing profile flags fragmented cadence.
 */
export function splitForStaggeredDelivery(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [trimmed];
  const sentences = trimmed.match(/[^.!?]+[.!?]+["')\]]?\s*/g);
  if (!sentences || sentences.length < 2) return [trimmed];
  const mid = Math.ceil(sentences.length / 2);
  const first = sentences.slice(0, mid).join("").trim();
  const second = sentences.slice(mid).join("").trim();
  if (!first || !second) return [trimmed];
  return [first, second];
}
